import { ReportTypeDefinition } from '../../../dto/advi/advi.dto';

const SECTION_LABELS: Record<string, string> = {
  summary: 'Samlet oversigt',
  path_overview: 'Oversigt over uddannelsesveje',
  required_modules: 'Påkrævede moduler',
  optional_modules: 'Valgfrie moduler',
  user_details: 'Detaljer for hver bruger',
  lagging_users: 'Brugere der falder bagud',
  maintenance_windows: 'Planlagt vedligehold',
  merit_notes: 'Noter om merit',
  module_summary: 'Tal pr. modul',
  participated: 'Har deltaget',
  planned: 'Tilmeldt / afventer',
  not_participated: 'Har ikke deltaget',
  merit_excluded: 'Brugere fritaget via merit',
  per_path_grouping: 'Opdelt pr. uddannelsesvej',
  org_summary: 'Tal for hele organisationen',
  dimension_charts: 'Diagrammer',
  location_breakdown: 'Fordeling pr. station',
  filter_summary: 'Valgte filtre (oHer psummering)',
  user_table: 'Brugerliste',
  path_completion_pct: 'Gennemførelsesgrad pr. uddannelsesvej',
  status_badge: 'Statusmarkering',
  merit_indicator: 'Merit-markering',
  matrix_table: 'Oversigt: modul og antal gennemførte',
  completion_counts: 'Antal gennemførte pr. modul',
  missing_highlight: 'Markér manglende gennemførelser',
  lagging_list: 'Brugere der falder bagud',
  missing_modules: 'Moduler de mangler',
  peer_comparison_note: 'Sammenligning med kolleger',
  merit_by_user: 'Merit pr. bruger',
  merit_by_path: 'Merit pr. uddannelsesvej',
  exempt_modules_note: 'Moduler brugeren er fritaget for',
  header_kpis: 'Nøgletal øverst på siden',
  chart_page: 'Diagramside: søjler pr. station, uddannelsesvej og modul',
  data_table_page: 'Talside: tabel med de samme tal',
};

function sections(ids: string[], defaults: Record<string, boolean> = {}) {
  return ids.map(id => ({ id, label: SECTION_LABELS[id] ?? id, default: defaults[id] ?? true }));
}

function dims(items: [string, string, boolean?][]) {
  return items.map(([id, label, def = true]) => ({ id, label, default: def }));
}

/**
 * Client-side fallback when GET /reports/types is unavailable (e.g. API not deployed yet).
 * Kept in sync with DUNA-API ReportTypeRegistry.php.
 */
export const REPORT_TYPE_DEFINITIONS: ReportTypeDefinition[] = [
  {
    id: 'event_report',
    name: 'Status-rapport',
    description: 'Omfattende rapport over uddannelsesveje, moduler og brugere der falder bagud.',
    category: 'detailed',
    default_schedule_mode: 'before_expiry',
    supports_advisering: true,
    sections: sections(['summary', 'path_overview', 'required_modules', 'optional_modules', 'user_details', 'lagging_users', 'maintenance_windows', 'merit_notes']),
    dimensions: dims([['per_path', 'Per uddannelsesvej'], ['per_station', 'Per station'], ['per_user', 'Per bruger']]),
    charts: [],
  },
  {
    id: 'module_participation',
    name: 'Moduldeltagelse',
    description: 'Deltagelse i specifikke moduler, opdelt pr. modul.',
    category: 'detailed',
    default_schedule_mode: 'calendar',
    supports_advisering: true,
    sections: sections(['module_summary', 'participated', 'planned', 'not_participated', 'merit_excluded', 'per_path_grouping']),
    dimensions: dims([['group_by_module', 'Gruppér pr. modul'], ['group_by_path', 'Gruppér pr. uddannelsesvej'], ['split_participated', 'Adskil deltaget/planlagt']]),
    charts: [],
  },
  {
    id: 'participation_snapshot',
    name: 'Deltagelses-oversigt',
    description: 'Grafisk oversigt med dimensioner og diagrammer.',
    category: 'detailed',
    default_schedule_mode: 'calendar',
    supports_advisering: true,
    sections: sections(['org_summary', 'dimension_charts', 'location_breakdown', 'filter_summary']),
    dimensions: dims([['per_organization', 'Per organisation'], ['per_station', 'Per station'], ['per_user', 'Per bruger']]),
    charts: [{ id: 'bar', label: 'Søjlediagram', default: true }, { id: 'pie', label: 'Cirkeldiagram', default: false }],
  },
  {
    id: 'user_progress_simple',
    name: 'Bruger-fremgang',
    description: 'Hurtigt overblik over brugeres fremgang pr. uddannelsesvej.',
    category: 'simple',
    default_schedule_mode: 'calendar',
    supports_advisering: true,
    sections: sections(['user_table', 'path_completion_pct', 'status_badge', 'merit_indicator']),
    dimensions: dims([['compact_layout', 'Kompakt layout', false], ['sort_by_completion', 'Sortér efter gennemførelse']]),
    charts: [],
  },
  {
    id: 'module_matrix_simple',
    name: 'Modul-oversigt',
    description: 'Simpel matrix: modul × antal gennemført.',
    category: 'simple',
    default_schedule_mode: 'calendar',
    supports_advisering: true,
    sections: sections(['matrix_table', 'completion_counts', 'missing_highlight']),
    dimensions: dims([['compact_layout', 'Kompakt layout']]),
    charts: [],
  },
  {
    id: 'lagging_only',
    name: 'Efterslæb',
    description: 'Kun brugere der falder bagud.',
    category: 'simple',
    default_schedule_mode: 'before_expiry',
    supports_advisering: true,
    sections: sections(['lagging_list', 'missing_modules', 'peer_comparison_note']),
    dimensions: dims([
      ['group_by_path', 'Gruppér pr. uddannelsesvej'],
      ['per_station', 'Per station'],
      ['per_organization', 'Samlet for valgte stationer', false],
    ]),
    charts: [],
  },
  {
    id: 'merit_summary',
    name: 'Merit-oversigt',
    description: 'Oversigt over brugere med merit.',
    category: 'simple',
    default_schedule_mode: 'calendar',
    supports_advisering: true,
    sections: sections(['merit_by_user', 'merit_by_path', 'exempt_modules_note']),
    dimensions: dims([['compact_layout', 'Kompakt layout']]),
    charts: [],
  },
  {
    id: 'minimal_onepager',
    name: 'Minimal one-pager',
    description: 'Søjlediagram på én side: pr. station, uddannelsesvej og modul, delt i deltaget / tilmeldt / ikke tilmeldt / merit.',
    category: 'minimal',
    default_schedule_mode: 'calendar',
    supports_advisering: true,
    sections: sections(['header_kpis', 'chart_page', 'data_table_page', 'filter_summary'], { data_table_page: false }),
    dimensions: dims([
      ['layer_participated', 'Lag: Deltaget'],
      ['layer_planned', 'Lag: Tilmeldt'],
      ['layer_not_participated', 'Lag: Ikke tilmeldt'],
      ['layer_merit', 'Lag: Merit'],
      ['show_value_labels', 'Vis værdier i søjlerne'],
      ['percent_mode', 'Vis som procent i stedet for antal', false],
      ['one_chart_per_module', 'Ét diagram pr. modul'],
      ['skip_empty_stations', 'Spring stationer over, hvor der er 0 på alle valgte uddannelsesveje'],
      ['exclude_secondary_users', 'Udeluk brugere med rollen sekundær på stationen (typisk primær på en anden station)', false],
      ['allow_multipage', 'Tillad flere sider (søjlerne fortsætter på næste side; en station deles ikke)'],
    ]),
    charts: [],
    options: [
      {
        id: 'sort_by',
        label: 'Sortér stationer efter',
        default: 'location_name',
        choices: [
          { value: 'location_name', label: 'Stationsnavn' },
          { value: 'participated', label: 'Deltaget' },
          { value: 'planned', label: 'Tilmeldt' },
          { value: 'not_participated', label: 'Ikke tilmeldt' },
          { value: 'merit', label: 'Merit' },
        ],
      },
      {
        id: 'sort_direction',
        label: 'Rækkefølge',
        default: 'asc',
        choices: [
          { value: 'asc', label: 'Stigende (ASC)' },
          { value: 'desc', label: 'Faldende (DESC)' },
        ],
      },
    ],
  },
];

export function getReportTypeDefinition(id: string): ReportTypeDefinition | undefined {
  return REPORT_TYPE_DEFINITIONS.find(t => t.id === id);
}
